import { GeoLayer, RadioSubLayers, SubLayers, SwitchSubLayers } from './Layer'
import BoreHoleLayer from './BoreHoles'
import BuriedValleysLayer from './BuriedValleys'
import PostGlacialSedimentThicknessLayer from './PostGlacialSedimentThickness'
import GroundPollution1Layer from './GroundPollution1'
import GroundPollution2Layer from './GroundPollution2'
import RawMaterialAreasLayer from './RawMaterialAreas'
import VarvLayer from './Varv'
import SeaLevelOnLandLayer from './SeaLevelOnLand'
import SliceKoteLayer from './SliceKote'
import SliceDHMLayer from './SliceDHM'
import BlueSpotExtremeRain from './BlueSpotExtremeRain'
import { DataTheme } from 'src/Enums'
import TerrainShadows from './TerrainShadows'
import GerdaLayer from './Gerda'
import OnshoreWindmillsLayer from './OnshoreWindmills'
import WaterExtractionPlantsLayer from './WaterExtractionPlants'
import ProtectedNatureTypesLayer from './ProtectedNatureTypes'
import ShallowGroundwaterLayer from './ShallowGroundwater'
import IceAgeLayer from './IceAge'
import WindmillsAtSeaLayer from './WindmillsAtSea'
import BirdProtectionLayer from './BirdProtection'
import RamsarLayer from './Ramsar'
import HabitatProtectionLayer from './HabitatProtection'
import ForestProtectionLayer from './ForestProtection'
import WildlifeLayer from './Wildlife'
import NaturaStateOwnedLayer from './NaturaStateOwned'
import NaturaLayer from './Natura'
import PreQuaternaryLithologyLayer from './PreQuaternaryLithology'
import PreQuaternaryElevationLayer from './PreQuaternaryElevation'
import ElevationAndDepthLayer from './ElevationAndDepth'
import SoilTypesLayer from './SoilTypes'
import MorphologyLayer from './Morphology'
import SeabedSedimentsLayer from './SeabedSediments'
import PesticideWaterworksLayer from './PesticideWaterworks'
import PesticideWaterSupplyWellsLayer from './PesticideWaterSupplyWells'
import ModelAreaLayer from './ModelAreaLayer'
import CostalLocations from './CostalLocations'
import { GeoModel } from '../geo/GeoModel'
import ScreenClassicLayer from './ScreenClassic'
import ScreenSubduedLayer from './ScreenSubdued'
import ScreenGreyLayer from './ScreenGrey'
import OrtoBackgroundLayer from './OrtoBackground'
import TopoLowBackgroundLayer from './TopoLowBackground'
import TopoHighBackgroundLayer from './TopoHighBackground'
import PrussianBackgroundLayer from './PrussianBackground'
import OSMBackgroundLayer from './OSMBackground'
import HydroModelLayer from './HydroModel'
import WindSpeedLayer from './WindSpeed'
import { ContextTag, WikiLocation } from '../wiki/Wiki'

export interface LayerCopyright {
  name: string
  url: string
  priority: number
}
interface LayerCopyrightType {
  [name: string]: LayerCopyright
}

const createLayerCopyrigts = <T extends LayerCopyrightType>(copyrights: T): T => copyrights
export const layerCopyrights = createLayerCopyrigts({
  OpenStreetMap: {
    name: 'OpenStreetMap',
    url: 'https://www.openstreetmap.org/',
    priority: 0
  },
  Geodatastyrelsen: {
    name: 'Geodatastyrelsen',
    url: 'https://gst.dk/',
    priority: 0
  },
  Geo: {
    name: 'Geo',
    url: 'https://www.geo.dk/forside/',
    priority: 1
  },
  Geus: {
    name: 'GEUS',
    url: 'https://www.geus.dk/',
    priority: 2
  },
  Miljoportal: {
    name: 'Miljøportal',
    url: 'https://arealeditering-dist-geo.miljoeportal.dk/',
    priority: 2
  },
  Varv: {
    name: 'Varv 1992',
    url: 'https://www.geus.dk/',
    priority: 2
  },
  MST: {
    name: 'Miljøstyrelsen',
    url: 'https://mst.dk/',
    priority: 2
  },
  SDFI: {
    name: 'Styrelsen for Dataforsyning og Infrastruktur',
    url: 'https://sdfi.dk/',
    priority: 2
  },
  Energistyrelsen: {
    name: 'Energistyrelsen',
    url: 'https://ens.dk/',
    priority: 2
  }
})

/**
 * Defining the layer groups
 */
export enum LayerGroup {
  GENERAL = 'GENERAL',
  NATURE = 'NATURE',
  SUBSURFACE = 'SUBSURFACE',
  WATER = 'WATER',
  WINDPOWER = 'WIND_POWER',
  POLLUTION = 'POLLUTION',
  GEOPHYSICS = 'GEOPHYSICS',
  ICE_AGE = 'ICE_AGE',
  NEAR_TERRAIN = 'NEAR_TERRAIN',
  BACKGROUND_MAPS = 'BACKGROUND_MAPS',
  POINTS_OF_INTEREST = 'POINTS_OF_INTEREST'
}

const defaultGroupOrdering: LayerGroup[] = [
  LayerGroup.GENERAL,
  LayerGroup.NATURE,
  LayerGroup.SUBSURFACE,
  LayerGroup.NEAR_TERRAIN,
  LayerGroup.GEOPHYSICS,
  LayerGroup.POLLUTION,
  LayerGroup.WATER,
  LayerGroup.WINDPOWER,
  LayerGroup.ICE_AGE,
  LayerGroup.BACKGROUND_MAPS
]

const themeGroupOrdering: Record<DataTheme, LayerGroup[]> = {
  [DataTheme.NONE]: defaultGroupOrdering,
  [DataTheme.ICE_AGE]: defaultGroupOrdering,
  [DataTheme.CLIMATE]: defaultGroupOrdering,
  [DataTheme.ENERGY]: defaultGroupOrdering,
  [DataTheme.ENVIRONMENT]: defaultGroupOrdering
}

/**
 * Defining the possible layers
 */
export enum LayerType {
  TERRAIN_SHADOWS = 'TERRAIN_SHADOWS',
  BORE_HOLES = 'BORE_HOLES',
  SEA_LEVEL_ON_LAND = 'SEA_LEVEL_ON_LAND',
  BLUE_SPOT_EXTREME_RAIN = 'BLUE_SPOT_EXTREME_RAIN',
  BURIED_VALLEYS = 'BURIED_VALLEYS',
  SLICE_KOTE = 'SLICE_KOTE',
  SLICE_DHM = 'SLICE_DHM',
  POSTGLACIAL_SEDIMENT_THICKNESS = 'POSTGLACIAL_SEDIMENT_THICKNESS',
  GROUND_POLLUTION_1 = 'GROUND_POLLUTION_1',
  GROUND_POLLUTION_2 = 'GROUND_POLLUTION_2',
  ICE_AGE = 'ICE_AGE',
  RAW_MATERIALS = 'RAW_MATERIALS',
  VARV = 'VARV',
  GERDA = 'GERDA',
  WATER_EXTRACTION_PLANTS = 'WATER_EXTRACTION_PLANTS',
  PROTECTED_NATURE_TYPES = 'PROTECTED_NATURE_TYPES',
  SHALLOW_GROUNDWATER = 'SHALLOW_GROUNDWATER',
  WINDMILLS_AT_SEA = 'WINDMILLS_AT_SEA',
  ONSHORE_WINDMILLS = 'ONSHORE_WINDMILLS',
  BIRD_PROTECTION = 'BIRD_PROTECTION',
  RAMSAR = 'RAMSAR',
  HABITAT_PROTECTION = 'HABITAT_PROTECTION',
  FOREST_PROTECTION = 'FOREST_PROTECTION',
  WILDLIFE = 'WILDLIFE',
  NATURA_STATE_OWNED = 'NATURA_STATE_OWNED',
  NATURA = 'NATURA',
  PREQUATERNARY_LITHOLOGY = 'PREQUATERNARY_LITHOLOGY',
  PREQUATERNARY_ELEVATION = 'PREQUATERNARY_ELEVATION',
  ELEVATION_DEPTH = 'ELEVATION_DEPTH',
  SOIL_TYPES = 'SOIL_TYPES',
  MORPHOLOGY = 'MORPHOLOGY',
  SEABED_SEDIMENTS = 'SEABED_SEDIMENTS',
  PESTICIDE_WATERWORKS = 'PESTICIDE_WATERWORKS',
  PESTICIDE_WATER_SUPPLY_WELLS = 'PESTICIDE_WATER_SUPPLY_WELLS',
  MODEL_AREAS = 'MODEL_AREAS',
  HYDRO_MODEL = 'HYDRO_MODEL',
  SCREEN_CLASSIC = 'SCREEN_CLASSIC',
  SCREEN_SUBDUED = 'SCREEN_SUBDUED',
  SCREEN_GREY = 'SCREEN_GREY',
  ORTO_BACKGROUND = 'ORTO_BACKGROUND',
  TOPO_LOW_BACKGROUND = 'TOPO_LOW_BACKGROUND',
  TOPO_HIGH_BACKGROUND = 'TOPO_HIGH_BACKGROUND',
  PRUSSIAN_BACKGROUND = 'PRUSSIAN_BACKGROUND',
  OSM_BACKGROUND = 'OSM_BACKGROUND',
  WIND_SPEED = 'WIND_SPEED',
  COSTAL_LOCATIONS = 'COSTAL_LOCATIONS'
}

const enabledLayers = new Map<LayerType, GeoLayer>()
const allLayerDescriptions = new Map<LayerType, LayerDescription>([
  [LayerType.TERRAIN_SHADOWS, TerrainShadows.describe()],
  [LayerType.BORE_HOLES, BoreHoleLayer.describe()],
  [LayerType.SEA_LEVEL_ON_LAND, SeaLevelOnLandLayer.describe()],
  [LayerType.BLUE_SPOT_EXTREME_RAIN, BlueSpotExtremeRain.describe()],
  [LayerType.BURIED_VALLEYS, BuriedValleysLayer.describe()],
  [LayerType.SLICE_KOTE, SliceKoteLayer.describe()],
  [LayerType.SLICE_DHM, SliceDHMLayer.describe()],
  [LayerType.POSTGLACIAL_SEDIMENT_THICKNESS, PostGlacialSedimentThicknessLayer.describe()],
  [LayerType.GROUND_POLLUTION_1, GroundPollution1Layer.describe()],
  [LayerType.GROUND_POLLUTION_2, GroundPollution2Layer.describe()],
  [LayerType.ICE_AGE, IceAgeLayer.describe()],
  [LayerType.RAW_MATERIALS, RawMaterialAreasLayer.describe()],
  [LayerType.VARV, VarvLayer.describe()],
  [LayerType.GERDA, GerdaLayer.describe()],
  [LayerType.WATER_EXTRACTION_PLANTS, WaterExtractionPlantsLayer.describe()],
  [LayerType.PROTECTED_NATURE_TYPES, ProtectedNatureTypesLayer.describe()],
  [LayerType.SHALLOW_GROUNDWATER, ShallowGroundwaterLayer.describe()],
  [LayerType.WINDMILLS_AT_SEA, WindmillsAtSeaLayer.describe()],
  [LayerType.ONSHORE_WINDMILLS, OnshoreWindmillsLayer.describe()],
  [LayerType.NATURA, NaturaLayer.describe()],
  [LayerType.BIRD_PROTECTION, BirdProtectionLayer.describe()],
  [LayerType.RAMSAR, RamsarLayer.describe()],
  [LayerType.HABITAT_PROTECTION, HabitatProtectionLayer.describe()],
  [LayerType.FOREST_PROTECTION, ForestProtectionLayer.describe()],
  [LayerType.WILDLIFE, WildlifeLayer.describe()],
  [LayerType.NATURA_STATE_OWNED, NaturaStateOwnedLayer.describe()],
  [LayerType.PREQUATERNARY_LITHOLOGY, PreQuaternaryLithologyLayer.describe()],
  [LayerType.PREQUATERNARY_ELEVATION, PreQuaternaryElevationLayer.describe()],
  [LayerType.ELEVATION_DEPTH, ElevationAndDepthLayer.describe()],
  [LayerType.SOIL_TYPES, SoilTypesLayer.describe()],
  [LayerType.MORPHOLOGY, MorphologyLayer.describe()],
  [LayerType.SEABED_SEDIMENTS, SeabedSedimentsLayer.describe()],
  [LayerType.PESTICIDE_WATERWORKS, PesticideWaterworksLayer.describe()],
  [LayerType.PESTICIDE_WATER_SUPPLY_WELLS, PesticideWaterSupplyWellsLayer.describe()],
  [LayerType.HYDRO_MODEL, HydroModelLayer.describe()],
  [LayerType.SCREEN_CLASSIC, ScreenClassicLayer.describe()],
  [LayerType.SCREEN_SUBDUED, ScreenSubduedLayer.describe()],
  [LayerType.SCREEN_GREY, ScreenGreyLayer.describe()],
  [LayerType.ORTO_BACKGROUND, OrtoBackgroundLayer.describe()],
  [LayerType.TOPO_LOW_BACKGROUND, TopoLowBackgroundLayer.describe()],
  [LayerType.TOPO_HIGH_BACKGROUND, TopoHighBackgroundLayer.describe()],
  [LayerType.PRUSSIAN_BACKGROUND, PrussianBackgroundLayer.describe()],
  [LayerType.OSM_BACKGROUND, OSMBackgroundLayer.describe()],
  [LayerType.MODEL_AREAS, ModelAreaLayer.describe()],
  [LayerType.WIND_SPEED, WindSpeedLayer.describe()],
  [LayerType.COSTAL_LOCATIONS, CostalLocations.describe()]
])

export interface LayerParams {
  layerType: LayerType
  params: LayerParamsObject
}

export interface LayerCapabilities {
  type: LayerType
  capabilities: ol.GlobalObject
}

export interface LayerParamsObject {
  opacity?: number
  depth?: number
  level?: number
  subLayers?: SubLayers
  radioSubLayers?: RadioSubLayers
  switchSubLayers?: SwitchSubLayers
}

export interface ImageLegend {
  legendImage: string
  imageWidth?: number
  imageHeight?: number
  legendTextId?: string
}

export interface LayerDescription {
  type: LayerType
  name: string
  group: LayerGroup
  ordering?: number
  contextTags: ContextTag[]
  themes: DataTheme[]
  capabilitiesUrl: string
  legendEnabled: boolean
  imageLegend?: ImageLegend
  opacityEnabled: boolean
  layerCopyright: LayerCopyright
}

export interface LayerConstructionOptions {
  token: string | undefined
  selectedGeoModel: string | undefined
  geoModels: GeoModel[] | undefined
  capabilities: ol.GlobalObject | undefined
  params: LayerParamsObject | undefined
  poi: WikiLocation[] | undefined
}

/**
 * A list of initial LayerParams for all layers
 */
export const initialLayerParams: LayerParams[] = [
  { layerType: LayerType.TERRAIN_SHADOWS, params: TerrainShadows.defaultParams },
  { layerType: LayerType.BURIED_VALLEYS, params: BuriedValleysLayer.defaultParams },
  { layerType: LayerType.BORE_HOLES, params: BoreHoleLayer.defaultParams },
  { layerType: LayerType.SEA_LEVEL_ON_LAND, params: SeaLevelOnLandLayer.defaultParams },
  { layerType: LayerType.BLUE_SPOT_EXTREME_RAIN, params: BlueSpotExtremeRain.defaultParams },
  { layerType: LayerType.SLICE_DHM, params: SliceDHMLayer.defaultParams },
  { layerType: LayerType.SLICE_KOTE, params: SliceKoteLayer.defaultParams },
  { layerType: LayerType.POSTGLACIAL_SEDIMENT_THICKNESS, params: PostGlacialSedimentThicknessLayer.defaultParams },
  { layerType: LayerType.GROUND_POLLUTION_1, params: GroundPollution1Layer.defaultParams },
  { layerType: LayerType.GROUND_POLLUTION_2, params: GroundPollution2Layer.defaultParams },
  { layerType: LayerType.ICE_AGE, params: IceAgeLayer.defaultParams },
  { layerType: LayerType.RAW_MATERIALS, params: RawMaterialAreasLayer.defaultParams },
  { layerType: LayerType.VARV, params: VarvLayer.defaultParams },
  { layerType: LayerType.GERDA, params: GerdaLayer.defaultParams },
  { layerType: LayerType.WATER_EXTRACTION_PLANTS, params: WaterExtractionPlantsLayer.defaultParams },
  { layerType: LayerType.PROTECTED_NATURE_TYPES, params: ProtectedNatureTypesLayer.defaultParams },
  { layerType: LayerType.SHALLOW_GROUNDWATER, params: ShallowGroundwaterLayer.defaultParams },
  { layerType: LayerType.WINDMILLS_AT_SEA, params: WindmillsAtSeaLayer.defaultParams },
  { layerType: LayerType.ONSHORE_WINDMILLS, params: OnshoreWindmillsLayer.defaultParams },
  { layerType: LayerType.BIRD_PROTECTION, params: BirdProtectionLayer.defaultParams },
  { layerType: LayerType.RAMSAR, params: RamsarLayer.defaultParams },
  { layerType: LayerType.HABITAT_PROTECTION, params: HabitatProtectionLayer.defaultParams },
  { layerType: LayerType.FOREST_PROTECTION, params: ForestProtectionLayer.defaultParams },
  { layerType: LayerType.WILDLIFE, params: WildlifeLayer.defaultParams },
  { layerType: LayerType.NATURA_STATE_OWNED, params: NaturaStateOwnedLayer.defaultParams },
  { layerType: LayerType.NATURA, params: NaturaLayer.defaultParams },
  { layerType: LayerType.PREQUATERNARY_LITHOLOGY, params: PreQuaternaryLithologyLayer.defaultParams },
  { layerType: LayerType.PREQUATERNARY_ELEVATION, params: PreQuaternaryElevationLayer.defaultParams },
  { layerType: LayerType.ELEVATION_DEPTH, params: ElevationAndDepthLayer.defaultParams },
  { layerType: LayerType.SOIL_TYPES, params: SoilTypesLayer.defaultParams },
  { layerType: LayerType.MORPHOLOGY, params: MorphologyLayer.defaultParams },
  { layerType: LayerType.SEABED_SEDIMENTS, params: SeabedSedimentsLayer.defaultParams },
  { layerType: LayerType.PESTICIDE_WATERWORKS, params: PesticideWaterworksLayer.defaultParams },
  { layerType: LayerType.PESTICIDE_WATER_SUPPLY_WELLS, params: PesticideWaterSupplyWellsLayer.defaultParams },
  { layerType: LayerType.HYDRO_MODEL, params: HydroModelLayer.defaultParams },
  { layerType: LayerType.SCREEN_CLASSIC, params: ScreenClassicLayer.defaultParams },
  { layerType: LayerType.SCREEN_SUBDUED, params: ScreenSubduedLayer.defaultParams },
  { layerType: LayerType.SCREEN_GREY, params: ScreenGreyLayer.defaultParams },
  { layerType: LayerType.ORTO_BACKGROUND, params: OrtoBackgroundLayer.defaultParams },
  { layerType: LayerType.TOPO_LOW_BACKGROUND, params: TopoLowBackgroundLayer.defaultParams },
  { layerType: LayerType.TOPO_HIGH_BACKGROUND, params: TopoHighBackgroundLayer.defaultParams },
  { layerType: LayerType.PRUSSIAN_BACKGROUND, params: PrussianBackgroundLayer.defaultParams },
  { layerType: LayerType.MODEL_AREAS, params: ModelAreaLayer.defaultParams },
  { layerType: LayerType.WIND_SPEED, params: WindSpeedLayer.defaultParams },
  { layerType: LayerType.COSTAL_LOCATIONS, params: CostalLocations.defaultParams }
]

/**
 * Ask the registry to create a new layer of the given type (name).
 * @param layerType The name of the layer to add.
 * @param options The options used when constructing the new layer.
 * @returns A Layer subclass.
 */
export const addLayerByType = (layerType: LayerType, options: any): GeoLayer => {
  if (!enabledLayers.has(layerType)) {
    switch (layerType) {
      case LayerType.TERRAIN_SHADOWS:
        enabledLayers.set(layerType, new TerrainShadows(options))
        break
      case LayerType.BORE_HOLES:
        enabledLayers.set(layerType, new BoreHoleLayer(options))
        break
      case LayerType.SEA_LEVEL_ON_LAND:
        enabledLayers.set(layerType, new SeaLevelOnLandLayer(options))
        break
      case LayerType.BLUE_SPOT_EXTREME_RAIN:
        enabledLayers.set(layerType, new BlueSpotExtremeRain(options))
        break
      case LayerType.BURIED_VALLEYS:
        enabledLayers.set(layerType, new BuriedValleysLayer(options))
        break
      case LayerType.GROUND_POLLUTION_1:
        enabledLayers.set(layerType, new GroundPollution1Layer(options))
        break
      case LayerType.GROUND_POLLUTION_2:
        enabledLayers.set(layerType, new GroundPollution2Layer(options))
        break
      case LayerType.RAW_MATERIALS:
        enabledLayers.set(layerType, new RawMaterialAreasLayer(options))
        break
      case LayerType.VARV:
        enabledLayers.set(layerType, new VarvLayer(options))
        break
      case LayerType.GERDA:
        enabledLayers.set(layerType, new GerdaLayer(options))
        break
      case LayerType.POSTGLACIAL_SEDIMENT_THICKNESS:
        enabledLayers.set(layerType, new PostGlacialSedimentThicknessLayer(options))
        break
      case LayerType.SLICE_KOTE:
        enabledLayers.set(layerType, new SliceKoteLayer(options))
        break
      case LayerType.SLICE_DHM:
        enabledLayers.set(layerType, new SliceDHMLayer(options))
        break
      case LayerType.WATER_EXTRACTION_PLANTS:
        enabledLayers.set(layerType, new WaterExtractionPlantsLayer(options))
        break
      case LayerType.PROTECTED_NATURE_TYPES:
        enabledLayers.set(layerType, new ProtectedNatureTypesLayer(options))
        break
      case LayerType.SHALLOW_GROUNDWATER:
        enabledLayers.set(layerType, new ShallowGroundwaterLayer(options))
        break
      case LayerType.ICE_AGE:
        enabledLayers.set(layerType, new IceAgeLayer(options))
        break
      case LayerType.WINDMILLS_AT_SEA:
        enabledLayers.set(layerType, new WindmillsAtSeaLayer(options))
        break
      case LayerType.ONSHORE_WINDMILLS:
        enabledLayers.set(layerType, new OnshoreWindmillsLayer(options))
        break
      case LayerType.BIRD_PROTECTION:
        enabledLayers.set(layerType, new BirdProtectionLayer(options))
        break
      case LayerType.RAMSAR:
        enabledLayers.set(layerType, new RamsarLayer(options))
        break
      case LayerType.HABITAT_PROTECTION:
        enabledLayers.set(layerType, new HabitatProtectionLayer(options))
        break
      case LayerType.FOREST_PROTECTION:
        enabledLayers.set(layerType, new ForestProtectionLayer(options))
        break
      case LayerType.WILDLIFE:
        enabledLayers.set(layerType, new WildlifeLayer(options))
        break
      case LayerType.NATURA_STATE_OWNED:
        enabledLayers.set(layerType, new NaturaStateOwnedLayer(options))
        break
      case LayerType.NATURA:
        enabledLayers.set(layerType, new NaturaLayer(options))
        break
      case LayerType.PREQUATERNARY_LITHOLOGY:
        enabledLayers.set(layerType, new PreQuaternaryLithologyLayer(options))
        break
      case LayerType.PREQUATERNARY_ELEVATION:
        enabledLayers.set(layerType, new PreQuaternaryElevationLayer(options))
        break
      case LayerType.ELEVATION_DEPTH:
        enabledLayers.set(layerType, new ElevationAndDepthLayer(options))
        break
      case LayerType.SOIL_TYPES:
        enabledLayers.set(layerType, new SoilTypesLayer(options))
        break
      case LayerType.MORPHOLOGY:
        enabledLayers.set(layerType, new MorphologyLayer(options))
        break
      case LayerType.SEABED_SEDIMENTS:
        enabledLayers.set(layerType, new SeabedSedimentsLayer(options))
        break
      case LayerType.PESTICIDE_WATERWORKS:
        enabledLayers.set(layerType, new PesticideWaterworksLayer(options))
        break
      case LayerType.PESTICIDE_WATER_SUPPLY_WELLS:
        enabledLayers.set(layerType, new PesticideWaterSupplyWellsLayer(options))
        break
      case LayerType.SCREEN_CLASSIC:
        enabledLayers.set(layerType, new ScreenClassicLayer(options))
        break
      case LayerType.SCREEN_SUBDUED:
        enabledLayers.set(layerType, new ScreenSubduedLayer(options))
        break
      case LayerType.SCREEN_GREY:
        enabledLayers.set(layerType, new ScreenGreyLayer(options))
        break
      case LayerType.ORTO_BACKGROUND:
        enabledLayers.set(layerType, new OrtoBackgroundLayer(options))
        break
      case LayerType.PRUSSIAN_BACKGROUND:
        enabledLayers.set(layerType, new PrussianBackgroundLayer(options))
        break
      case LayerType.TOPO_HIGH_BACKGROUND:
        enabledLayers.set(layerType, new TopoHighBackgroundLayer(options))
        break
      case LayerType.TOPO_LOW_BACKGROUND:
        enabledLayers.set(layerType, new TopoLowBackgroundLayer(options))
        break
      case LayerType.OSM_BACKGROUND:
        enabledLayers.set(layerType, new OSMBackgroundLayer(options))
        break
      case LayerType.HYDRO_MODEL:
        enabledLayers.set(layerType, new HydroModelLayer(options))
        break
      case LayerType.MODEL_AREAS:
        enabledLayers.set(layerType, new ModelAreaLayer(options))
        break
      case LayerType.WIND_SPEED:
        enabledLayers.set(layerType, new WindSpeedLayer(options))
        break
      case LayerType.COSTAL_LOCATIONS:
        enabledLayers.set(layerType, new CostalLocations(options))
        break
    }
  }

  const layer = enabledLayers.get(layerType)
  if (layer === undefined || layer === null) {
    throw new Error(`Layer name ${layerType as string} given.`)
  }

  return layer
}

/**
 * Remove an active GeoLayer from the registry.
 * @param layerType The type of the layer to remove from the list of active GeoLayers.
 */
export const removeLayerByType = (layerType: LayerType): boolean => {
  if (enabledLayers.has(layerType)) {
    enabledLayers.delete(layerType)
    return true
  }
  return false
}

/**
 * Get the named layer from the registry.
 * @param layerType Name of the layer to fetch.
 * @returns A GeoLater instance.
 */
export const getGeoLayerByType = (layerType: LayerType): GeoLayer => {
  const layer = enabledLayers.get(layerType)
  if (layer === undefined || layer === null) {
    throw new Error(`Asked for non-existing layer with type ${layerType as string},`)
  }
  return layer
}

/**
 * Gets the available LayerType's for a given DataTheme
 * @param dataTheme The DataTheme for which to get the available LayerType's
 * @returns A list of available LayerType's
 */
export const getLayersByDataTheme = (dataTheme: DataTheme): LayerType[] => {
  return Object.values(LayerType)
    .map(layerType => getLayerDescriptionForType(layerType))
    .filter(layerDescription => {
      return (dataTheme === DataTheme.NONE || layerDescription.themes.includes(dataTheme))
    })
    .sort((a, b) => {
      const aOrder = a.ordering !== undefined ? a.ordering : Number.MAX_SAFE_INTEGER
      const bOrder = b.ordering !== undefined ? b.ordering : Number.MAX_SAFE_INTEGER
      return aOrder - bOrder
    })
    .map(layerDescription => layerDescription.type)
}

/**
 * Get sub layer name of a given value.
 * @param params Params to get sub layer name from.
 * @param value Sub layer value.
 * @param type Name of the laye from which to get the sub layer name
 * @returns Name of sub layer.
 */
export const getSublayerName = (params: LayerParamsObject, value: number, type: LayerType): string => {
  return getGeoLayerByType(type).getSubLayerName(params, value)
}

/**
 * Gets the LayerGroup's for a given DataTheme
 * @param dataTheme The DataTheme for which to get the LayerGroup's
 * @returns A list of LayerGroup's
 */
export const getLayerGroups = (dataTheme: DataTheme): LayerGroup[] => {
  const layers = getLayersByDataTheme(dataTheme)
  const themeGroupOrder = themeGroupOrdering[dataTheme]
  return layers
    .map(layer => getLayerDescriptionForType(layer).group)
    .filter((value, index, self) => self.indexOf(value) === index) // remove duplicates
    .sort((a: LayerGroup, b: LayerGroup) => {
      return themeGroupOrder.indexOf(a) - themeGroupOrder.indexOf(b)
    })
}

/**
 * Gets the description of a LayerType
 * @param layerType the LayerType to be descriped
 * @returns A LayerDescription
 */
export const getLayerDescriptionForType = (layerType: LayerType): LayerDescription => {
  const description = allLayerDescriptions.get(layerType)
  if (description === undefined) {
    throw new Error(`Unknown layer type ${layerType as string} requested`)
  }
  return description
}

/**
 * Delete all layers from the registry.
 */
export const clearLayers = (): void => {
  enabledLayers.clear()
}
